import type { HistoricalPosition } from '@ema-atr/shared';

const buckets = [
  ['< −10%', 'negative'], ['[−10%, −5%)', 'negative'], ['[−5%, −2%)', 'negative'], ['[−2%, −1%)', 'negative'], ['[−1%, 0%)', 'negative'],
  ['0%', 'neutral'], ['(0%, 1%]', 'positive'], ['(1%, 2%]', 'positive'], ['(2%, 5%]', 'positive'], ['(5%, 10%]', 'positive'], ['> 10%', 'positive']
] as const;

export function summarizeReturns(values: number[]) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  const count = sorted.length;
  return {
    count,
    average: count ? sorted.reduce((sum, value) => sum + value / count, 0) : null,
    median: count ? count % 2 ? sorted[Math.floor(count / 2)]! : sorted[count / 2 - 1]! / 2 + sorted[count / 2]! / 2 : null,
    best: sorted.at(-1) ?? null, worst: sorted[0] ?? null
  };
}

export function averageOutcomes(values: number[]) {
  const wins = values.filter((value) => Number.isFinite(value) && value > 0);
  const losses = values.filter((value) => Number.isFinite(value) && value < 0);
  return { averageWin: summarizeReturns(wins).average, averageLoss: summarizeReturns(losses).average };
}

/** Values must be in close-time order. Zero and invalid returns interrupt a streak. */
export function returnStreaks(values: number[]) {
  type Streak = { count: number; totalReturn: number; start: number; end: number };
  const empty = (): Streak => ({ count: 0, totalReturn: 0, start: -1, end: -1 });
  let longestLoss = empty(); let longestWin = empty();
  let run = empty(); let sign = 0;
  for (const [index, value] of values.entries()) {
    const nextSign = Number.isFinite(value) ? Math.sign(value) : 0;
    if (!nextSign) { run = empty(); sign = 0; continue; }
    run = nextSign === sign ? { ...run, count: run.count + 1, totalReturn: run.totalReturn + value, end: index } : { count: 1, totalReturn: value, start: index, end: index };
    sign = nextSign;
    if (sign < 0 && (run.count > longestLoss.count || (run.count === longestLoss.count && run.totalReturn < longestLoss.totalReturn))) longestLoss = { ...run };
    if (sign > 0 && (run.count > longestWin.count || (run.count === longestWin.count && run.totalReturn > longestWin.totalReturn))) longestWin = { ...run };
  }
  return { longestLoss, longestWin };
}

export function positionPriceReturn(position: Pick<HistoricalPosition, 'symbol' | 'positionSide' | 'openPrice' | 'closePrice'>): number | null {
  if (!position.symbol.endsWith('-USDT') || !['long', 'short'].includes(position.positionSide) || !position.openPrice?.trim() || !position.closePrice?.trim()) return null;
  const open = Number(position.openPrice); const close = Number(position.closePrice);
  if (!Number.isFinite(open) || !Number.isFinite(close) || open <= 0 || close <= 0) return null;
  const value = (close - open) / open * (position.positionSide === 'short' ? -100 : 100);
  return Number.isFinite(value) ? value === 0 ? 0 : value : null;
}

export function returnHistogram(values: number[]) {
  const result = buckets.map(([label, tone]) => ({ label, tone, count: 0, percentage: 0 }));
  let count = 0;
  for (const value of values) {
    if (!Number.isFinite(value)) continue;
    const index = value < 0 ? [-10, -5, -2, -1, 0].findIndex((end) => value < end) : value === 0 ? 5 : 6 + [1, 2, 5, 10, Infinity].findIndex((end) => value <= end);
    result[index]!.count += 1;
    count += 1;
  }
  for (const bucket of result) bucket.percentage = count ? bucket.count / count * 100 : 0;
  return result;
}

export function returnLabel(value: number | null): string {
  if (value === null) return '—';
  const rounded = Number(value.toFixed(2));
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(2)}%`;
}
export function holdingLabel(value: number | null): string {
  if (value === null) return '时长未知';
  const minutes = value / 60000;
  if (minutes < 1) return '不足 1 分钟';
  if (minutes < 60) return `${Math.round(minutes)} 分钟`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)} 小时`;
  return `${(minutes / 1440).toFixed(1)} 天`;
}
